import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CalendarIcon, Plus, Edit2, Trash2, Calculator, Shield, Check, Users, Calendar as CalendarIcon2, Lock, CheckCircle, Download, RotateCcw, AlertTriangle, Eye, Megaphone, User, BarChart3 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { cn } from '../lib/utils';

const statusColors = {
    'offen': 'text-green-600',
    'geschlossen': 'text-yellow-600',
    'ausgewertet': 'text-blue-600'
};

const statusLabels = {
    'offen': 'Offen',
    'geschlossen': 'Geschlossen',
    'ausgewertet': 'Ausgewertet'
};

export default function AdminPage() {
    const { api, isAdmin, user } = useAuth();
    const [matches, setMatches] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calculating, setCalculating] = useState(false);
    
    // Form states
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showResultDialog, setShowResultDialog] = useState(false);
    const [showUserPredictionDialog, setShowUserPredictionDialog] = useState(false);
    const [showAddUserDialog, setShowAddUserDialog] = useState(false);
    const [showPredictionsDialog, setShowPredictionsDialog] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [matchPredictions, setMatchPredictions] = useState(null);
    const [userPredictions, setUserPredictions] = useState(null);
    const [showUserPredictionsDialog, setShowUserPredictionsDialog] = useState(false);
    const [showBulkTipDialog, setShowBulkTipDialog] = useState(false);
    const [bulkTipUser, setBulkTipUser] = useState(null);
    const [bulkTipInputs, setBulkTipInputs] = useState({});
    const [bulkTipExisting, setBulkTipExisting] = useState({});
    const [savingBulkTips, setSavingBulkTips] = useState(false);
    const [showUserDetailsDialog, setShowUserDetailsDialog] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [loadingUserDetails, setLoadingUserDetails] = useState(false);
    const [editingUser, setEditingUser] = useState(false);
    const [userEditForm, setUserEditForm] = useState({});
    const [savingUserEdit, setSavingUserEdit] = useState(false);
    
    // New match form
    const [homeTeam, setHomeTeam] = useState('');
    const [awayTeam, setAwayTeam] = useState('');
    const [matchDate, setMatchDate] = useState(null);
    const [matchTime, setMatchTime] = useState('');
    const [matchTimezone, setMatchTimezone] = useState('Europe/Zurich');
    const [matchStatus, setMatchStatus] = useState('offen');
    
    // Timezone options for Switzerland/Europe
    const timezoneOptions = [
        { value: 'Europe/Zurich', label: 'MEZ/MESZ (Bern/Zürich)', offset: '+1/+2' },
        { value: 'Europe/Berlin', label: 'MEZ/MESZ (Berlin)', offset: '+1/+2' },
        { value: 'Europe/Vienna', label: 'MEZ/MESZ (Wien)', offset: '+1/+2' },
        { value: 'Europe/London', label: 'GMT/BST (London)', offset: '+0/+1' },
        { value: 'UTC', label: 'UTC', offset: '+0' },
    ];
    
    // Convert local time to UTC based on timezone
    const convertToUTC = (localTime, timezone) => {
        if (!localTime) return null;
        // Create a date object for today with the local time
        const today = new Date();
        const [hours, minutes] = localTime.split(':').map(Number);
        
        // Create date string with timezone
        const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}T${localTime}:00`;
        
        // Parse as local time in the specified timezone
        const localDate = new Date(dateStr);
        const utcDate = new Date(localDate.toLocaleString('en-US', { timeZone: 'UTC' }));
        const tzDate = new Date(localDate.toLocaleString('en-US', { timeZone: timezone }));
        
        // Calculate offset
        const offset = (tzDate - utcDate) / (1000 * 60); // offset in minutes
        
        // Adjust hours and minutes
        let utcHours = hours - Math.floor(offset / 60);
        let utcMinutes = minutes - (offset % 60);
        
        if (utcMinutes < 0) {
            utcMinutes += 60;
            utcHours -= 1;
        }
        if (utcMinutes >= 60) {
            utcMinutes -= 60;
            utcHours += 1;
        }
        if (utcHours < 0) utcHours += 24;
        if (utcHours >= 24) utcHours -= 24;
        
        return `${String(utcHours).padStart(2, '0')}:${String(utcMinutes).padStart(2, '0')}`;
    };
    
    // Convert UTC time to local time based on timezone  
    const convertFromUTC = (utcTime, timezone) => {
        if (!utcTime) return '';
        const [hours, minutes] = utcTime.split(':').map(Number);
        
        // Create a UTC date
        const today = new Date();
        const utcDate = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes));
        
        // Convert to local timezone
        const localStr = utcDate.toLocaleTimeString('de-CH', { 
            timeZone: timezone, 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        });
        
        return localStr;
    };
    
    // Result form
    const [homeScore, setHomeScore] = useState('');
    const [awayScore, setAwayScore] = useState('');
    
    // User prediction form
    const [selectedMatchId, setSelectedMatchId] = useState('');
    const [userHomeScore, setUserHomeScore] = useState('');
    const [userAwayScore, setUserAwayScore] = useState('');
    
    // New user form
    const [newUserFirstName, setNewUserFirstName] = useState('');
    const [newUserLastName, setNewUserLastName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [newUserPseudonym, setNewUserPseudonym] = useState('');
    
    // Announcement
    const [announcement, setAnnouncement] = useState('');
    const [announcementLoading, setAnnouncementLoading] = useState(false);
    
    // Email settings
    const [ccEmails, setCcEmails] = useState('');
    const [regEmails, setRegEmails] = useState('');
    const [ccEmailsLoading, setCcEmailsLoading] = useState(false);
    
    // Statistics
    const [statistics, setStatistics] = useState([]);
    const [statisticsLoading, setStatisticsLoading] = useState(false);

    useEffect(() => {
        fetchData();
        fetchAnnouncement();
        fetchEmailSettings();
    }, [api]);

    const fetchEmailSettings = async () => {
        try {
            const res = await api().get('/admin/email-settings');
            setRegEmails(res.data.registration_emails.join(', '));
            setCcEmails(res.data.tips_cc_emails.join(', '));
        } catch (error) {
            console.error('Failed to fetch email settings:', error);
        }
    };

    const fetchStatistics = async () => {
        setStatisticsLoading(true);
        try {
            const res = await api().get('/admin/statistics');
            setStatistics(res.data);
        } catch (error) {
            console.error('Failed to fetch statistics:', error);
            toast.error('Fehler beim Laden der Statistik');
        } finally {
            setStatisticsLoading(false);
        }
    };

    const handleSaveEmailSettings = async () => {
        setCcEmailsLoading(true);
        try {
            const regList = regEmails.split(',').map(e => e.trim()).filter(e => e);
            const ccList = ccEmails.split(',').map(e => e.trim()).filter(e => e);
            await api().put('/admin/email-settings', { registration_emails: regList, tips_cc_emails: ccList });
            toast.success('Email-Einstellungen gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setCcEmailsLoading(false);
        }
    };

    const handleToggleAdmin = async (userId, userName) => {
        if (!window.confirm(`Admin-Rechte für "${userName}" ändern?`)) return;
        try {
            const res = await api().post(`/admin/toggle-admin/${userId}`);
            toast.success(res.data.message);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Ändern der Admin-Rechte');
        }
    };

    const fetchAnnouncement = async () => {
        try {
            const res = await api().get('/announcement');
            setAnnouncement(res.data.text || '');
        } catch (error) {
            console.error('Failed to fetch announcement:', error);
        }
    };

    const handleSaveAnnouncement = async () => {
        setAnnouncementLoading(true);
        try {
            await api().put('/admin/announcement', { text: announcement });
            toast.success('Ankündigung gespeichert');
        } catch (error) {
            toast.error('Fehler beim Speichern');
        } finally {
            setAnnouncementLoading(false);
        }
    };

    const openBulkTipDialog = async (user) => {
        setBulkTipUser(user);
        setBulkTipInputs({});
        setBulkTipExisting({});
        setShowBulkTipDialog(true);
        
        // Load existing predictions for this user
        try {
            const res = await api().get(`/admin/predictions/${user.id}`);
            const existing = {};
            res.data.forEach(pred => {
                existing[pred.match_id] = {
                    home: pred.home_score !== null && pred.home_score !== undefined ? pred.home_score.toString() : '',
                    away: pred.away_score !== null && pred.away_score !== undefined ? pred.away_score.toString() : ''
                };
            });
            setBulkTipExisting(existing);
            setBulkTipInputs(existing);
        } catch (error) {
            console.error('Failed to load user predictions:', error);
            setBulkTipExisting({});
            setBulkTipInputs({});
        }
    };

    const handleBulkTipChange = (matchId, field, value) => {
        setBulkTipInputs(prev => ({
            ...prev,
            [matchId]: {
                ...prev[matchId],
                [field]: value
            }
        }));
    };

    const handleSaveBulkTips = async () => {
        if (!bulkTipUser) return;
        
        setSavingBulkTips(true);
        let savedCount = 0;
        const evaluatedMatchIds = [];
        
        try {
            for (const [matchId, scores] of Object.entries(bulkTipInputs)) {
                const homeScore = scores.home;
                const awayScore = scores.away;
                
                // Skip if both fields are empty
                if (homeScore === '' && awayScore === '') continue;
                
                // Both must be filled
                if (homeScore === '' || awayScore === '') continue;
                
                await api().post('/admin/prediction', {
                    user_id: bulkTipUser.id,
                    match_id: matchId,
                    home_score: parseInt(homeScore),
                    away_score: parseInt(awayScore)
                });
                savedCount++;
                
                // Track which evaluated matches were updated
                const match = matches.find(m => m.id === matchId);
                if (match && match.status === 'ausgewertet') {
                    evaluatedMatchIds.push(matchId);
                }
            }
            
            // Recalculate points for updated evaluated matches
            if (evaluatedMatchIds.length > 0) {
                for (const matchId of evaluatedMatchIds) {
                    try {
                        await api().post(`/calculate-match/${matchId}`);
                    } catch (e) {
                        console.error('Failed to recalculate match:', matchId, e);
                    }
                }
                toast.success(`${savedCount} Tipp(s) gespeichert und ${evaluatedMatchIds.length} Spiel(e) neu ausgewertet`);
            } else if (savedCount > 0) {
                toast.success(`${savedCount} Tipp(s) für ${bulkTipUser.pseudonym || bulkTipUser.first_name} gespeichert`);
            } else {
                toast.info('Keine Änderungen');
            }
            setShowBulkTipDialog(false);
            fetchData(); // Refresh data
        } catch (error) {
            toast.error('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
        } finally {
            setSavingBulkTips(false);
        }
    };

    const openUserDetailsDialog = async (userId) => {
        setShowUserDetailsDialog(true);
        setLoadingUserDetails(true);
        setUserDetails(null);
        setEditingUser(false);
        try {
            const res = await api().get(`/admin/users/${userId}/details`);
            setUserDetails(res.data);
            setUserEditForm({
                first_name: res.data.first_name || '',
                last_name: res.data.last_name || '',
                pseudonym: res.data.pseudonym || '',
                email: res.data.email || '',
                address: res.data.address || '',
                supported_player: res.data.supported_player || '',
                amount_per_goal: res.data.amount_per_goal || '',
                password: ''  // Empty by default, only update if filled
            });
        } catch (error) {
            console.error('Failed to load user details:', error);
            toast.error('Fehler beim Laden der Benutzerdaten');
        } finally {
            setLoadingUserDetails(false);
        }
    };

    const handleSaveUserEdit = async () => {
        if (!userDetails) return;
        
        setSavingUserEdit(true);
        try {
            const updateData = {};
            if (userEditForm.first_name !== userDetails.first_name) updateData.first_name = userEditForm.first_name;
            if (userEditForm.last_name !== userDetails.last_name) updateData.last_name = userEditForm.last_name;
            if (userEditForm.pseudonym !== userDetails.pseudonym) updateData.pseudonym = userEditForm.pseudonym;
            if (userEditForm.email !== userDetails.email) updateData.email = userEditForm.email;
            if (userEditForm.address !== (userDetails.address || '')) updateData.address = userEditForm.address;
            if (userEditForm.supported_player !== (userDetails.supported_player || '')) updateData.supported_player = userEditForm.supported_player;
            
            const amountVal = userEditForm.amount_per_goal === '' ? null : parseFloat(userEditForm.amount_per_goal);
            if (amountVal !== userDetails.amount_per_goal) updateData.amount_per_goal = amountVal;
            
            // Only send password if it's been filled in
            if (userEditForm.password && userEditForm.password.trim() !== '') {
                updateData.password = userEditForm.password;
            }
            
            if (Object.keys(updateData).length === 0) {
                toast.info('Keine Änderungen');
                setEditingUser(false);
                return;
            }
            
            await api().put(`/admin/users/${userDetails.id}`, updateData);
            toast.success('Benutzerdaten gespeichert');
            
            // Reload user details
            const res = await api().get(`/admin/users/${userDetails.id}/details`);
            setUserDetails(res.data);
            setEditingUser(false);
            fetchData(); // Refresh user list
        } catch (error) {
            toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
        } finally {
            setSavingUserEdit(false);
        }
    };

    const fetchData = async () => {
        try {
            const [matchesRes, usersRes] = await Promise.all([
                api().get('/matches'),
                api().get('/admin/users')
            ]);
            setMatches(matchesRes.data);
            setUsers(usersRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddMatch = async () => {
        if (!homeTeam || !awayTeam || !matchDate) {
            toast.error('Bitte alle Felder ausfüllen');
            return;
        }

        try {
            // Convert local time to UTC before saving
            const utcTime = matchTime ? convertToUTC(matchTime, matchTimezone) : null;
            
            await api().post('/matches', {
                home_team: homeTeam,
                away_team: awayTeam,
                match_date: format(matchDate, 'yyyy-MM-dd'),
                match_time: utcTime,
                match_timezone: matchTime ? matchTimezone : null
            });
            toast.success('Spiel erstellt');
            setShowAddDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
        }
    };

    const handleEditMatch = async () => {
        if (!selectedMatch) return;

        try {
            // Convert local time to UTC before saving
            const utcTime = matchTime ? convertToUTC(matchTime, matchTimezone) : null;
            
            await api().put(`/matches/${selectedMatch.id}`, {
                home_team: homeTeam || undefined,
                away_team: awayTeam || undefined,
                match_date: matchDate ? format(matchDate, 'yyyy-MM-dd') : undefined,
                match_time: utcTime,
                match_timezone: matchTime ? matchTimezone : null,
                status: matchStatus || undefined
            });
            toast.success('Spiel aktualisiert');
            setShowEditDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
        }
    };

    const handleSetResult = async () => {
        if (!selectedMatch) return;

        const home = parseInt(homeScore);
        const away = parseInt(awayScore);

        if (isNaN(home) || isNaN(away) || home < 0 || away < 0) {
            toast.error('Ungültige Scores');
            return;
        }

        try {
            await api().put(`/matches/${selectedMatch.id}/result`, {
                home_score: home,
                away_score: away
            });
            toast.success('Resultat gespeichert');
            setShowResultDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
    };

    const handleDeleteMatch = async (matchId) => {
        if (!window.confirm('Spiel wirklich löschen?')) return;

        try {
            await api().delete(`/matches/${matchId}`);
            toast.success('Spiel gelöscht');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const handleDeleteUser = async (userId, userName) => {
        if (!window.confirm(`Benutzer "${userName}" wirklich löschen? Alle Tipps werden ebenfalls gelöscht.`)) return;

        try {
            await api().delete(`/admin/users/${userId}`);
            toast.success('Benutzer gelöscht');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const handleMakeAdmin = async (userEmail) => {
        if (!window.confirm(`${userEmail} zum Admin machen?`)) return;

        try {
            await api().post(`/admin/make-admin/${userEmail}`);
            toast.success('Admin-Rechte erteilt');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler');
        }
    };

    const handleResetUserPoints = async (userId, userName) => {
        if (!window.confirm(`Alle Berechnungen für "${userName}" wirklich zurücksetzen?`)) return;

        try {
            await api().post(`/admin/reset-user-points/${userId}`);
            toast.success('Punkte zurückgesetzt');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
        }
    };

    const handleResetAllPoints = async () => {
        if (!window.confirm('ACHTUNG: Wirklich ALLE Berechnungen für ALLE Benutzer zurücksetzen?')) return;
        if (!window.confirm('Diese Aktion kann nicht rückgängig gemacht werden. Fortfahren?')) return;

        try {
            await api().post('/admin/reset-all-points');
            toast.success('Alle Berechnungen zurückgesetzt');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
        }
    };

    const handleResetMatch = async (matchId, matchName) => {
        if (!window.confirm(`Berechnung für "${matchName}" wirklich zurücksetzen?`)) return;

        try {
            await api().post(`/admin/reset-match/${matchId}`);
            toast.success('Spiel-Berechnung zurückgesetzt');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Zurücksetzen');
        }
    };

    const handleDeleteAllPredictions = async () => {
        if (!window.confirm('ACHTUNG: Wirklich ALLE Tipps für ALLE Benutzer löschen?')) return;
        if (!window.confirm('Diese Aktion kann nicht rückgängig gemacht werden. Fortfahren?')) return;

        try {
            const response = await api().delete('/admin/predictions/all');
            toast.success(response.data.message);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const handleDeleteUserPredictions = async (userId, userName) => {
        if (!window.confirm(`Alle Tipps von "${userName}" wirklich löschen?`)) return;

        try {
            const response = await api().delete(`/admin/predictions/user/${userId}`);
            toast.success(response.data.message);
            fetchData();
            // Close dialog if open
            if (showUserPredictionsDialog) {
                setShowUserPredictionsDialog(false);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
        }
    };

    const handleUserPrediction = async () => {
        if (!selectedUser || !selectedMatchId) {
            toast.error('Bitte Spiel auswählen');
            return;
        }

        const home = parseInt(userHomeScore);
        const away = parseInt(userAwayScore);

        if (isNaN(home) || isNaN(away) || home < 0 || away < 0) {
            toast.error('Ungültige Scores');
            return;
        }

        try {
            await api().post(`/admin/predictions?user_id=${selectedUser.id}`, {
                match_id: selectedMatchId,
                home_score: home,
                away_score: away
            });
            toast.success('Tipp gespeichert');
            setShowUserPredictionDialog(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        }
    };

    const handleAddUser = async () => {
        if (!newUserFirstName || !newUserLastName || !newUserEmail || !newUserPassword) {
            toast.error('Bitte alle Pflichtfelder ausfüllen');
            return;
        }

        try {
            await api().post('/admin/users', {
                first_name: newUserFirstName,
                last_name: newUserLastName,
                email: newUserEmail,
                password: newUserPassword,
                pseudonym: newUserPseudonym || `${newUserFirstName} ${newUserLastName}`
            });
            toast.success('Benutzer erstellt');
            setShowAddUserDialog(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
        }
    };

    const handleCalculateMatch = async (matchId) => {
        setCalculating(true);
        try {
            const response = await api().post(`/calculate-match/${matchId}`);
            toast.success(response.data.message);
            fetchData();
            // Refresh predictions dialog if open
            if (showPredictionsDialog && matchPredictions?.match?.id === matchId) {
                await openPredictionsDialog(matchId);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler bei der Berechnung');
        } finally {
            setCalculating(false);
        }
    };

    const openPredictionsDialog = async (matchId) => {
        try {
            const response = await api().get(`/match-predictions/${matchId}`);
            setMatchPredictions(response.data);
            setShowPredictionsDialog(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Laden der Tipps');
        }
    };

    const openUserPredictionsDialog = async (userId) => {
        try {
            const response = await api().get(`/user-predictions/${userId}`);
            setUserPredictions(response.data);
            setShowUserPredictionsDialog(true);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Laden der Tipps');
        }
    };

    const handleExport = async (type) => {
        try {
            const response = await api().get(`/admin/export/${type}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const filenames = {
                'leaderboard': 'rangliste.csv',
                'matches': 'spiele.csv',
                'predictions': 'tipps.csv',
                'users': 'benutzer.csv'
            };
            
            link.setAttribute('download', filenames[type] || 'export.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Export erfolgreich');
        } catch (error) {
            toast.error('Export fehlgeschlagen');
        }
    };

    const resetForm = () => {
        setHomeTeam('');
        setAwayTeam('');
        setMatchDate(null);
        setMatchTime('');
        setMatchTimezone('Europe/Zurich');
        setHomeScore('');
        setAwayScore('');
        setSelectedMatch(null);
        setSelectedUser(null);
        setSelectedMatchId('');
        setUserHomeScore('');
        setUserAwayScore('');
        setNewUserFirstName('');
        setNewUserLastName('');
        setNewUserEmail('');
        setNewUserPassword('');
        setNewUserPseudonym('');
    };

    const openEditDialog = (match) => {
        setSelectedMatch(match);
        setHomeTeam(match.home_team);
        setAwayTeam(match.away_team);
        setMatchDate(parseISO(match.match_date));
        // Convert UTC time back to local time for display
        const tz = match.match_timezone || 'Europe/Zurich';
        setMatchTimezone(tz);
        setMatchTime(match.match_time ? convertFromUTC(match.match_time, tz) : '');
        setMatchStatus(match.status || 'offen');
        setShowEditDialog(true);
    };

    const openResultDialog = (match) => {
        setSelectedMatch(match);
        setHomeScore(match.home_score?.toString() || '');
        setAwayScore(match.away_score?.toString() || '');
        setShowResultDialog(true);
    };

    const openUserPredictionDialog = (userObj) => {
        setSelectedUser(userObj);
        setSelectedMatchId('');
        setUserHomeScore('');
        setUserAwayScore('');
        setShowUserPredictionDialog(true);
    };

    if (!isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                <Shield className="w-16 h-16 text-muted-foreground mb-4" />
                <h2 className="text-2xl font-heading">Zugriff verweigert</h2>
                <p className="text-muted-foreground">Diese Seite ist nur für Administratoren</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="spinner" />
            </div>
        );
    }

    const openMatches = matches.filter(m => !m.is_finished);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="admin-page">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-heading">Admin Panel</h1>
                    <p className="text-muted-foreground">Spiele und Benutzer verwalten</p>
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" data-testid="export-button">
                                <Download className="w-4 h-4 mr-2" />
                                CSV Export
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-48" align="end">
                            <div className="space-y-1">
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => handleExport('leaderboard')}
                                    data-testid="export-leaderboard"
                                >
                                    Rangliste
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => handleExport('matches')}
                                    data-testid="export-matches"
                                >
                                    Spiele
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => handleExport('predictions')}
                                    data-testid="export-predictions"
                                >
                                    Alle Tipps
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start"
                                    onClick={() => handleExport('users')}
                                    data-testid="export-users"
                                >
                                    Benutzer
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            <Tabs defaultValue="matches" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-6">
                    <TabsTrigger value="matches" data-testid="matches-tab">
                        <CalendarIcon2 className="w-4 h-4 mr-2" />
                        Spiele ({matches.length})
                    </TabsTrigger>
                    <TabsTrigger value="users" data-testid="users-tab">
                        <Users className="w-4 h-4 mr-2" />
                        Benutzer ({users.length})
                    </TabsTrigger>
                    <TabsTrigger value="statistics" data-testid="statistics-tab" onClick={() => { if (statistics.length === 0) fetchStatistics(); }}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Statistik
                    </TabsTrigger>
                    <TabsTrigger value="announcement" data-testid="announcement-tab">
                        <Megaphone className="w-4 h-4 mr-2" />
                        Ankündigung
                    </TabsTrigger>
                </TabsList>

                {/* MATCHES TAB */}
                <TabsContent value="matches">
                    <Card data-testid="matches-table">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Alle Spiele</CardTitle>
                            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                                <DialogTrigger asChild>
                                    <Button className="btn-primary" data-testid="add-match-button">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Neues Spiel
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Neues Spiel erstellen</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        <div className="space-y-2">
                                            <Label>Heimteam</Label>
                                            <Input
                                                value={homeTeam}
                                                onChange={(e) => setHomeTeam(e.target.value)}
                                                placeholder="z.B. SC Brühl"
                                                data-testid="home-team-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Auswärtsteam</Label>
                                            <Input
                                                value={awayTeam}
                                                onChange={(e) => setAwayTeam(e.target.value)}
                                                placeholder="z.B. FC St. Gallen II"
                                                data-testid="away-team-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Datum</Label>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className={cn(
                                                            "w-full justify-start text-left font-normal",
                                                            !matchDate && "text-muted-foreground"
                                                        )}
                                                        data-testid="match-date-picker"
                                                    >
                                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                                        {matchDate ? format(matchDate, 'PPP', { locale: de }) : 'Datum wählen'}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={matchDate}
                                                        onSelect={setMatchDate}
                                                        locale={de}
                                                        initialFocus
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Uhrzeit (Lokalzeit)</Label>
                                            <div className="flex gap-2">
                                                <Input
                                                    type="time"
                                                    value={matchTime}
                                                    onChange={(e) => setMatchTime(e.target.value)}
                                                    placeholder="z.B. 16:30"
                                                    data-testid="match-time-input"
                                                    className="flex-1"
                                                />
                                                <select
                                                    value={matchTimezone}
                                                    onChange={(e) => setMatchTimezone(e.target.value)}
                                                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                                    data-testid="match-timezone-select"
                                                >
                                                    {timezoneOptions.map(tz => (
                                                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <p className="text-xs text-muted-foreground">Optional. Wird intern als UTC gespeichert.</p>
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <DialogClose asChild>
                                            <Button variant="ghost">Abbrechen</Button>
                                        </DialogClose>
                                        <Button onClick={handleAddMatch} className="btn-primary" data-testid="save-match-button">
                                            Erstellen
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Datum</TableHead>
                                        <TableHead>Uhrzeit</TableHead>
                                        <TableHead>Heimteam</TableHead>
                                        <TableHead>Auswärtsteam</TableHead>
                                        <TableHead className="text-center">Resultat</TableHead>
                                        <TableHead className="text-center">Status</TableHead>
                                        <TableHead className="text-right">Aktionen</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {matches.map((match) => (
                                        <TableRow key={match.id} data-testid={`admin-match-row-${match.id}`}>
                                            <TableCell>
                                                {format(parseISO(match.match_date), 'dd.MM.yyyy', { locale: de })}
                                            </TableCell>
                                            <TableCell>
                                                {match.match_time ? convertFromUTC(match.match_time, match.match_timezone || 'Europe/Zurich') : '-'}
                                            </TableCell>
                                            <TableCell className="font-medium">{match.home_team}</TableCell>
                                            <TableCell className="font-medium">{match.away_team}</TableCell>
                                            <TableCell className="text-center tabular-nums">
                                                {match.home_score !== null ? `${match.home_score} : ${match.away_score}` : '-'}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <span className={statusColors[match.status]}>
                                                    {statusLabels[match.status]}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => openPredictionsDialog(match.id)}
                                                        title="Tipps anzeigen"
                                                        data-testid={`view-predictions-${match.id}`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => openEditDialog(match)}
                                                        title="Bearbeiten"
                                                        data-testid={`edit-match-${match.id}`}
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => openResultDialog(match)}
                                                        title="Resultat eintragen"
                                                        data-testid={`set-result-${match.id}`}
                                                    >
                                                        <Calculator className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-[#00ad43] hover:text-[#00ad43]/80"
                                                        onClick={() => handleCalculateMatch(match.id)}
                                                        title="Punkte berechnen"
                                                        data-testid={`calculate-match-${match.id}`}
                                                        disabled={calculating || match.home_score === null}
                                                    >
                                                        <CheckCircle className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-orange-600 hover:text-orange-700"
                                                        onClick={() => handleResetMatch(match.id, `${match.home_team} vs ${match.away_team}`)}
                                                        title="Berechnung zurücksetzen"
                                                        data-testid={`reset-match-${match.id}`}
                                                        disabled={match.status !== 'ausgewertet'}
                                                    >
                                                        <RotateCcw className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-destructive hover:text-destructive"
                                                        onClick={() => handleDeleteMatch(match.id)}
                                                        title="Löschen"
                                                        data-testid={`delete-match-${match.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {matches.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                Noch keine Spiele erstellt
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* USERS TAB */}
                <TabsContent value="users">
                    <Card data-testid="users-table">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle>Alle Benutzer</CardTitle>
                            <div className="flex gap-2">
                                <Button 
                                    variant="outline"
                                    className="text-red-600 border-red-300 hover:bg-red-50"
                                    onClick={handleDeleteAllPredictions}
                                    data-testid="delete-all-predictions-button"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Alle Tipps löschen
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                    onClick={handleResetAllPoints}
                                    data-testid="reset-all-points-button"
                                >
                                    <AlertTriangle className="w-4 h-4 mr-2" />
                                    Alle Berechnungen löschen
                                </Button>
                                <Button 
                                    className="btn-primary" 
                                    onClick={() => setShowAddUserDialog(true)}
                                    data-testid="add-user-button"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Neuer Benutzer
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Pseudonym</TableHead>
                                        <TableHead>E-Mail</TableHead>
                                        <TableHead className="text-center">Status</TableHead>
                                        <TableHead className="text-center">Punkte</TableHead>
                                        <TableHead className="text-center">Rolle</TableHead>
                                        <TableHead className="text-right">Aktionen</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map((u) => (
                                        <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                                            <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                                            <TableCell>{u.pseudonym}</TableCell>
                                            <TableCell>{u.email}</TableCell>
                                            <TableCell className="text-center">
                                                {u.status === 'aktiv' ? (
                                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Aktiv</Badge>
                                                ) : u.status === 'inaktiv' ? (
                                                    <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200">Inaktiv</Badge>
                                                ) : u.status === 'angemeldet' ? (
                                                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">Angemeldet</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Aktiv</Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-center tabular-nums font-bold">{u.total_points}</TableCell>
                                            <TableCell className="text-center">
                                                {u.is_admin ? (
                                                    <span className="text-amber-600 font-medium">Admin</span>
                                                ) : (
                                                    <span className="text-muted-foreground">Benutzer</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    {!u.is_admin && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => openUserPredictionsDialog(u.id)}
                                                            title="Tipps anzeigen"
                                                            data-testid={`view-user-predictions-${u.id}`}
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="text-blue-600 hover:text-blue-700"
                                                        onClick={() => openUserDetailsDialog(u.id)}
                                                        title="Benutzerdaten anzeigen"
                                                        data-testid={`view-user-details-${u.id}`}
                                                    >
                                                        <User className="w-4 h-4" />
                                                    </Button>
                                                    {!u.is_admin && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-[#00ad43] hover:text-[#00ad43]/80"
                                                            onClick={() => openBulkTipDialog(u)}
                                                            title="Alle Tipps erfassen"
                                                            data-testid={`bulk-tip-${u.id}`}
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {!u.is_admin && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-red-600 hover:text-red-700"
                                                            onClick={() => handleDeleteUserPredictions(u.id, `${u.first_name} ${u.last_name}`)}
                                                            title="Alle Tipps löschen"
                                                            data-testid={`delete-predictions-${u.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {!u.is_admin && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-orange-600 hover:text-orange-700"
                                                            onClick={() => handleResetUserPoints(u.id, `${u.first_name} ${u.last_name}`)}
                                                            title="Punkte zurücksetzen"
                                                            data-testid={`reset-points-${u.id}`}
                                                        >
                                                            <RotateCcw className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {u.id !== user?.id && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className={u.is_admin ? "text-yellow-600 hover:text-yellow-700" : ""}
                                                            onClick={() => handleToggleAdmin(u.id, u.pseudonym || `${u.first_name} ${u.last_name}`)}
                                                            title={u.is_admin ? "Admin-Rechte entziehen" : "Zum Admin machen"}
                                                            data-testid={`toggle-admin-${u.id}`}
                                                        >
                                                            <Shield className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {u.id !== user?.id && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="text-destructive hover:text-destructive"
                                                            onClick={() => handleDeleteUser(u.id, `${u.first_name} ${u.last_name}`)}
                                                            title="Löschen"
                                                            data-testid={`delete-user-${u.id}`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {users.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                Keine Benutzer vorhanden
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* STATISTICS TAB */}
                <TabsContent value="statistics">
                    <Card data-testid="statistics-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <BarChart3 className="w-5 h-5" />
                                Punkte-Statistik pro Teilnehmer
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {statisticsLoading ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="spinner" />
                                </div>
                            ) : statistics.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">Keine Daten vorhanden</p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Teilnehmer</TableHead>
                                            <TableHead className="text-center">Gesamt</TableHead>
                                            <TableHead className="text-center bg-green-50 dark:bg-green-900/20">10 Pkt</TableHead>
                                            <TableHead className="text-center bg-green-50/70 dark:bg-green-900/15">9 Pkt</TableHead>
                                            <TableHead className="text-center bg-green-50/50 dark:bg-green-900/10">8 Pkt</TableHead>
                                            <TableHead className="text-center bg-yellow-50 dark:bg-yellow-900/10">6 Pkt</TableHead>
                                            <TableHead className="text-center bg-gray-50 dark:bg-gray-800/30">5 Pkt</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {statistics.map((stat) => (
                                            <TableRow key={stat.user_id}>
                                                <TableCell className="font-medium">{stat.pseudonym}</TableCell>
                                                <TableCell className="text-center font-bold text-[#00ad43]">{stat.total_points}</TableCell>
                                                <TableCell className="text-center bg-green-50 dark:bg-green-900/20">
                                                    {stat.tips_10_points > 0 ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#00ad43] text-white text-xs font-bold">
                                                            {stat.tips_10_points}
                                                        </span>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell className="text-center bg-green-50/70 dark:bg-green-900/15">
                                                    {stat.tips_9_points > 0 ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#00ad43]/80 text-white text-xs font-bold">
                                                            {stat.tips_9_points}
                                                        </span>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell className="text-center bg-green-50/50 dark:bg-green-900/10">
                                                    {stat.tips_8_points > 0 ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#00ad43]/60 text-white text-xs font-bold">
                                                            {stat.tips_8_points}
                                                        </span>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell className="text-center bg-yellow-50 dark:bg-yellow-900/10">
                                                    {stat.tips_6_points > 0 ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-yellow-500 text-white text-xs font-bold">
                                                            {stat.tips_6_points}
                                                        </span>
                                                    ) : '-'}
                                                </TableCell>
                                                <TableCell className="text-center bg-gray-50 dark:bg-gray-800/30">
                                                    {stat.tips_5_points > 0 ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-400 text-white text-xs font-bold">
                                                            {stat.tips_5_points}
                                                        </span>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ANNOUNCEMENT TAB */}
                <TabsContent value="announcement">
                    <div className="space-y-6">
                        <Card data-testid="announcement-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Megaphone className="w-5 h-5" />
                                    Ankündigung auf Startseite
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="announcement-text">Freitext (wird unterhalb des Headers angezeigt)</Label>
                                    <Textarea
                                        id="announcement-text"
                                        placeholder="Text hier eingeben... (leer lassen = keine Ankündigung)"
                                        value={announcement}
                                        onChange={(e) => setAnnouncement(e.target.value)}
                                        className="min-h-[150px]"
                                        data-testid="announcement-textarea"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Wird die Karte leer gelassen, wird sie auf der Startseite nicht angezeigt.<br />
                                        <strong>Links:</strong> Verwenden Sie <code className="bg-muted px-1 rounded">[Linktext](https://url.ch)</code> für klickbare Links.
                                    </p>
                                </div>
                                <Button 
                                    onClick={handleSaveAnnouncement} 
                                    disabled={announcementLoading}
                                    className="btn-primary"
                                    data-testid="save-announcement-button"
                                >
                                    {announcementLoading ? 'Speichern...' : 'Ankündigung speichern'}
                                </Button>
                            </CardContent>
                        </Card>

                        <Card data-testid="email-settings-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    Email-Einstellungen
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="reg-emails">Registrierungs-Email an:</Label>
                                    <Input
                                        id="reg-emails"
                                        placeholder="email1@example.com, email2@example.com"
                                        value={regEmails}
                                        onChange={(e) => setRegEmails(e.target.value)}
                                        data-testid="reg-emails-input"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Empfänger-Liste für Benachrichtigungen bei neuen Registrierungen. Komma-getrennt.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cc-emails">Tipp-Email an Admin (CC):</Label>
                                    <Input
                                        id="cc-emails"
                                        placeholder="email1@example.com, email2@example.com"
                                        value={ccEmails}
                                        onChange={(e) => setCcEmails(e.target.value)}
                                        data-testid="cc-emails-input"
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        CC-Empfänger wenn ein Benutzer Tipps speichert. Der Benutzer erhält die Email direkt. Komma-getrennt.
                                    </p>
                                </div>
                                <Button 
                                    onClick={handleSaveEmailSettings} 
                                    disabled={ccEmailsLoading}
                                    className="btn-primary"
                                    data-testid="save-email-settings-button"
                                >
                                    {ccEmailsLoading ? 'Speichern...' : 'Email-Einstellungen speichern'}
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Edit Match Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Spiel bearbeiten</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Heimteam</Label>
                            <Input
                                value={homeTeam}
                                onChange={(e) => setHomeTeam(e.target.value)}
                                data-testid="edit-home-team-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Auswärtsteam</Label>
                            <Input
                                value={awayTeam}
                                onChange={(e) => setAwayTeam(e.target.value)}
                                data-testid="edit-away-team-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Datum</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !matchDate && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {matchDate ? format(matchDate, 'PPP', { locale: de }) : 'Datum wählen'}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={matchDate}
                                        onSelect={setMatchDate}
                                        locale={de}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                        <div className="space-y-2">
                            <Label>Uhrzeit (Lokalzeit)</Label>
                            <div className="flex gap-2">
                                <Input
                                    type="time"
                                    value={matchTime}
                                    onChange={(e) => setMatchTime(e.target.value)}
                                    placeholder="z.B. 16:30"
                                    data-testid="edit-match-time-input"
                                    className="flex-1"
                                />
                                <select
                                    value={matchTimezone}
                                    onChange={(e) => setMatchTimezone(e.target.value)}
                                    className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    data-testid="edit-match-timezone-select"
                                >
                                    {timezoneOptions.map(tz => (
                                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                                    ))}
                                </select>
                            </div>
                            <p className="text-xs text-muted-foreground">Optional. Wird intern als UTC gespeichert.</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Status</Label>
                            <select
                                value={matchStatus}
                                onChange={(e) => setMatchStatus(e.target.value)}
                                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                data-testid="edit-match-status"
                            >
                                <option value="offen">Offen</option>
                                <option value="geschlossen">Geschlossen</option>
                                <option value="ausgewertet">Ausgewertet</option>
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost">Abbrechen</Button>
                        </DialogClose>
                        <Button onClick={handleEditMatch} className="btn-primary" data-testid="update-match-button">
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Result Dialog */}
            <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Resultat eintragen</DialogTitle>
                    </DialogHeader>
                    {selectedMatch && (
                        <div className="py-4">
                            <p className="text-center text-lg font-heading mb-6">
                                {selectedMatch.home_team} vs {selectedMatch.away_team}
                            </p>
                            <div className="flex items-center justify-center gap-4">
                                <div className="text-center">
                                    <Label className="text-xs">{selectedMatch.home_team}</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="99"
                                        value={homeScore}
                                        onChange={(e) => setHomeScore(e.target.value)}
                                        className="score-input mt-1"
                                        data-testid="result-home-score"
                                    />
                                </div>
                                <span className="text-2xl text-muted-foreground mt-6">:</span>
                                <div className="text-center">
                                    <Label className="text-xs">{selectedMatch.away_team}</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max="99"
                                        value={awayScore}
                                        onChange={(e) => setAwayScore(e.target.value)}
                                        className="score-input mt-1"
                                        data-testid="result-away-score"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost">Abbrechen</Button>
                        </DialogClose>
                        <Button onClick={handleSetResult} className="btn-primary" data-testid="save-result-button">
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* User Prediction Dialog */}
            <Dialog open={showUserPredictionDialog} onOpenChange={setShowUserPredictionDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Tipp für {selectedUser?.pseudonym} erfassen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Spiel auswählen</Label>
                            <Select value={selectedMatchId} onValueChange={setSelectedMatchId}>
                                <SelectTrigger data-testid="select-match-for-user">
                                    <SelectValue placeholder="Spiel wählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {matches.map((m) => (
                                        <SelectItem key={m.id} value={m.id}>
                                            {format(parseISO(m.match_date), 'dd.MM.')} - {m.home_team} vs {m.away_team}
                                            {m.is_finished && ' (Beendet)'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center justify-center gap-4">
                            <div className="text-center">
                                <Label className="text-xs">Heim</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="99"
                                    value={userHomeScore}
                                    onChange={(e) => setUserHomeScore(e.target.value)}
                                    className="score-input mt-1"
                                    data-testid="user-home-score"
                                />
                            </div>
                            <span className="text-2xl text-muted-foreground mt-6">:</span>
                            <div className="text-center">
                                <Label className="text-xs">Auswärts</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="99"
                                    value={userAwayScore}
                                    onChange={(e) => setUserAwayScore(e.target.value)}
                                    className="score-input mt-1"
                                    data-testid="user-away-score"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost">Abbrechen</Button>
                        </DialogClose>
                        <Button onClick={handleUserPrediction} className="btn-primary" data-testid="save-user-prediction">
                            Speichern
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add User Dialog */}
            <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Neuer Benutzer</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Vorname *</Label>
                                <Input
                                    value={newUserFirstName}
                                    onChange={(e) => {
                                        setNewUserFirstName(e.target.value);
                                        if (!newUserPseudonym) {
                                            setNewUserPseudonym(`${e.target.value} ${newUserLastName}`.trim());
                                        }
                                    }}
                                    placeholder="Max"
                                    data-testid="new-user-firstname"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nachname *</Label>
                                <Input
                                    value={newUserLastName}
                                    onChange={(e) => {
                                        setNewUserLastName(e.target.value);
                                        if (!newUserPseudonym || newUserPseudonym === `${newUserFirstName} `.trim()) {
                                            setNewUserPseudonym(`${newUserFirstName} ${e.target.value}`.trim());
                                        }
                                    }}
                                    placeholder="Muster"
                                    data-testid="new-user-lastname"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Pseudonym (Anzeigename in Rangliste)</Label>
                            <Input
                                value={newUserPseudonym}
                                onChange={(e) => setNewUserPseudonym(e.target.value)}
                                placeholder="z.B. Tippkönig2024"
                                data-testid="new-user-pseudonym"
                            />
                            <p className="text-xs text-muted-foreground">Leer = Vorname Nachname</p>
                        </div>
                        <div className="space-y-2">
                            <Label>E-Mail *</Label>
                            <Input
                                type="email"
                                value={newUserEmail}
                                onChange={(e) => setNewUserEmail(e.target.value)}
                                placeholder="max@example.ch"
                                data-testid="new-user-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Passwort *</Label>
                            <Input
                                type="password"
                                value={newUserPassword}
                                onChange={(e) => setNewUserPassword(e.target.value)}
                                placeholder="••••••••"
                                data-testid="new-user-password"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost">Abbrechen</Button>
                        </DialogClose>
                        <Button onClick={handleAddUser} className="btn-primary" data-testid="save-new-user">
                            Erstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Predictions Dialog */}
            <Dialog open={showPredictionsDialog} onOpenChange={setShowPredictionsDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Tipps für Spiel</DialogTitle>
                    </DialogHeader>
                    {matchPredictions && (
                        <div className="py-4">
                            <div className="text-center mb-4 p-3 bg-muted rounded-lg">
                                <p className="font-heading text-lg">
                                    {matchPredictions.match.home_team} vs {matchPredictions.match.away_team}
                                </p>
                                {matchPredictions.match.result && (
                                    <p className="text-2xl font-heading text-primary mt-1">
                                        Resultat: {matchPredictions.match.result}
                                    </p>
                                )}
                                <p className="text-sm text-muted-foreground mt-1">
                                    Status: {statusLabels[matchPredictions.match.status]}
                                </p>
                            </div>
                            
                            {matchPredictions.predictions.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Teilnehmer</TableHead>
                                            <TableHead className="text-center">Tipp</TableHead>
                                            <TableHead className="text-center">Punkte</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {matchPredictions.predictions.map((pred) => (
                                            <TableRow key={pred.user_id}>
                                                <TableCell className="font-medium">{pred.pseudonym}</TableCell>
                                                <TableCell className="text-center font-mono">{pred.prediction}</TableCell>
                                                <TableCell className="text-center">
                                                    {matchPredictions.match.status === 'ausgewertet' ? (
                                                        <span className="font-heading text-lg text-primary">{pred.points}</span>
                                                    ) : matchPredictions.match.result ? (
                                                        <span className="text-muted-foreground">
                                                            ({pred.calculated_points})
                                                        </span>
                                                    ) : (
                                                        <span className="text-muted-foreground">-</span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <p className="text-center text-muted-foreground py-8">
                                    Keine Tipps für dieses Spiel vorhanden
                                </p>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        {matchPredictions?.match.result && matchPredictions?.match.status !== 'ausgewertet' && (
                            <Button 
                                onClick={() => handleCalculateMatch(matchPredictions.match.id)}
                                disabled={calculating}
                                className="btn-primary"
                            >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Punkte berechnen
                            </Button>
                        )}
                        <DialogClose asChild>
                            <Button variant="ghost">Schliessen</Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* User Predictions Dialog */}
            <Dialog open={showUserPredictionsDialog} onOpenChange={setShowUserPredictionsDialog}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Tipps von {userPredictions?.user?.pseudonym}</DialogTitle>
                    </DialogHeader>
                    {userPredictions && (
                        <div className="flex-1 overflow-auto">
                            <div className="text-center mb-4 p-3 bg-muted rounded-lg">
                                <p className="text-2xl font-heading text-primary">
                                    {userPredictions.user.total_points} Punkte
                                </p>
                            </div>
                            
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Datum</TableHead>
                                        <TableHead>Spiel</TableHead>
                                        <TableHead className="text-center">Resultat</TableHead>
                                        <TableHead className="text-center">Tipp</TableHead>
                                        <TableHead className="text-center">Punkte</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {userPredictions.matches.map((m) => (
                                        <TableRow key={m.match_id}>
                                            <TableCell className="text-sm">
                                                {format(parseISO(m.match_date), 'dd.MM.yy', { locale: de })}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {m.home_team} - {m.away_team}
                                            </TableCell>
                                            <TableCell className="text-center font-mono">
                                                {m.result || '-'}
                                            </TableCell>
                                            <TableCell className="text-center font-mono">
                                                {m.prediction}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {typeof m.points === 'number' ? (
                                                    <span className="font-heading text-primary">{m.points}</span>
                                                ) : (
                                                    <span className="text-muted-foreground">{m.points}</span>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="ghost">Schliessen</Button>
                        </DialogClose>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk Tip Dialog - All matches for a user */}
            <Dialog open={showBulkTipDialog} onOpenChange={setShowBulkTipDialog}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit2 className="w-5 h-5" />
                            Tipps erfassen: {bulkTipUser?.pseudonym || `${bulkTipUser?.first_name} ${bulkTipUser?.last_name}`}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-y-auto">
                        <Table>
                            <TableHeader className="sticky top-0 bg-background z-10">
                                <TableRow>
                                    <TableHead className="w-28">Datum</TableHead>
                                    <TableHead>Heim</TableHead>
                                    <TableHead>Gast</TableHead>
                                    <TableHead className="w-20 text-center">Resultat</TableHead>
                                    <TableHead className="w-24 text-center">Tipp</TableHead>
                                    <TableHead className="w-20 text-center">Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {matches
                                    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
                                    .map(match => (
                                    <TableRow 
                                        key={match.id}
                                        className={match.status === 'ausgewertet' ? 'bg-green-50 dark:bg-green-950/20' : ''}
                                    >
                                        <TableCell className="text-sm">
                                            {format(parseISO(match.match_date), 'dd.MM.yy', { locale: de })}
                                        </TableCell>
                                        <TableCell className="font-medium text-sm">{match.home_team}</TableCell>
                                        <TableCell className="text-sm">{match.away_team}</TableCell>
                                        <TableCell className="text-center text-sm">
                                            {match.status === 'ausgewertet' ? (
                                                <span className="font-bold text-green-600">{match.home_score}:{match.away_score}</span>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center justify-center gap-1">
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="20"
                                                    className="w-12 h-8 text-center text-sm p-1"
                                                    value={bulkTipInputs[match.id]?.home || ''}
                                                    onChange={(e) => handleBulkTipChange(match.id, 'home', e.target.value)}
                                                    data-testid={`bulk-home-${match.id}`}
                                                />
                                                <span className="text-muted-foreground">:</span>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="20"
                                                    className="w-12 h-8 text-center text-sm p-1"
                                                    value={bulkTipInputs[match.id]?.away || ''}
                                                    onChange={(e) => handleBulkTipChange(match.id, 'away', e.target.value)}
                                                    data-testid={`bulk-away-${match.id}`}
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {bulkTipExisting[match.id] ? (
                                                <Check className="w-4 h-4 text-green-500 mx-auto" />
                                            ) : (
                                                <span className="text-gray-400 text-xs">-</span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    
                    <DialogFooter className="border-t pt-4">
                        <DialogClose asChild>
                            <Button variant="ghost">Abbrechen</Button>
                        </DialogClose>
                        <Button 
                            onClick={handleSaveBulkTips} 
                            disabled={savingBulkTips}
                            className="btn-primary"
                            data-testid="save-bulk-tips"
                        >
                            {savingBulkTips ? 'Speichern...' : 'Alle Tipps speichern'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* User Details Dialog */}
            <Dialog open={showUserDetailsDialog} onOpenChange={(open) => { setShowUserDetailsDialog(open); if (!open) setEditingUser(false); }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            Benutzerdaten {editingUser && '- Bearbeiten'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {loadingUserDetails ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="spinner" />
                        </div>
                    ) : userDetails ? (
                        editingUser ? (
                            /* Edit Mode */
                            <div className="space-y-4 py-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Pseudonym (Anzeigename)</Label>
                                        <Input
                                            value={userEditForm.pseudonym}
                                            onChange={(e) => setUserEditForm(prev => ({ ...prev, pseudonym: e.target.value }))}
                                            placeholder="Anzeigename in Rangliste"
                                            data-testid="edit-user-pseudonym"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Neues Passwort</Label>
                                        <Input
                                            type="password"
                                            value={userEditForm.password}
                                            onChange={(e) => setUserEditForm(prev => ({ ...prev, password: e.target.value }))}
                                            placeholder="Leer lassen für keine Änderung"
                                            data-testid="edit-user-password"
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-1">
                                    <Label className="text-xs">Email</Label>
                                    <Input
                                        type="email"
                                        value={userEditForm.email}
                                        onChange={(e) => setUserEditForm(prev => ({ ...prev, email: e.target.value }))}
                                    />
                                </div>
                                
                                <div className="space-y-1">
                                    <Label className="text-xs">Adresse, PLZ, Ort</Label>
                                    <Input
                                        value={userEditForm.address}
                                        onChange={(e) => setUserEditForm(prev => ({ ...prev, address: e.target.value }))}
                                        placeholder="Strasse, PLZ Ort"
                                    />
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label className="text-xs">Unterstützter Spieler/Trainer</Label>
                                        <Input
                                            value={userEditForm.supported_player}
                                            onChange={(e) => setUserEditForm(prev => ({ ...prev, supported_player: e.target.value }))}
                                            placeholder="Optional"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label className="text-xs">Betrag pro Tor (CHF)</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={userEditForm.amount_per_goal}
                                            onChange={(e) => setUserEditForm(prev => ({ ...prev, amount_per_goal: e.target.value }))}
                                            placeholder="z.B. 5.00"
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* View Mode */
                            <div className="space-y-4 py-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Name / Firma</Label>
                                        <p className="font-medium">{userDetails.pseudonym || `${userDetails.first_name} ${userDetails.last_name}`}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Email</Label>
                                        <p className="font-medium">{userDetails.email}</p>
                                    </div>
                                </div>
                                
                                <div>
                                    <Label className="text-xs text-muted-foreground">Adresse</Label>
                                    <p className="font-medium">{userDetails.address || '-'}</p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Unterstützter Spieler/Trainer</Label>
                                        <p className="font-medium">{userDetails.supported_player || '-'}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Betrag pro Tor</Label>
                                        <p className="font-medium">{userDetails.amount_per_goal ? `CHF ${userDetails.amount_per_goal.toFixed(2)}` : '-'}</p>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Punkte</Label>
                                        <p className="font-medium text-[#00ad43]">{userDetails.total_points}</p>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Admin</Label>
                                        <p className="font-medium">{userDetails.is_admin ? 'Ja' : 'Nein'}</p>
                                    </div>
                                </div>
                                
                                {userDetails.created_at && (
                                    <div>
                                        <Label className="text-xs text-muted-foreground">Registriert am</Label>
                                        <p className="font-medium">
                                            {format(parseISO(userDetails.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <p className="text-center text-muted-foreground py-8">Keine Daten gefunden</p>
                    )}
                    
                    <DialogFooter>
                        {editingUser ? (
                            <>
                                <Button variant="ghost" onClick={() => setEditingUser(false)}>Abbrechen</Button>
                                <Button 
                                    className="btn-primary"
                                    onClick={handleSaveUserEdit}
                                    disabled={savingUserEdit}
                                >
                                    {savingUserEdit ? 'Speichern...' : 'Speichern'}
                                </Button>
                            </>
                        ) : (
                            <>
                                <DialogClose asChild>
                                    <Button variant="ghost">Schliessen</Button>
                                </DialogClose>
                                {userDetails && !userDetails.is_admin && (
                                    <Button 
                                        variant="outline"
                                        onClick={() => setEditingUser(true)}
                                    >
                                        <Edit2 className="w-4 h-4 mr-2" />
                                        Bearbeiten
                                    </Button>
                                )}
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
